import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import Media from 'react-media';
import {Button} from '../../../../common/components';
import { Card, CardText } from 'react-toolbox/lib/card';
import Dropdown from 'react-toolbox/lib/dropdown';
import FontIcon from 'react-toolbox/lib/font_icon';
import { Tab, Tabs } from 'react-toolbox/lib/tabs';
import {
  MembershipType,
  MembershipStatus,
} from '../../../../common/option-lists';
import { formatZipCode } from '../../../../common/components/Form';
import { getCurrentUser } from '../../../../redux-modules/current-user';
import ButtonLink from '../../../../common/components/ButtonLink';
import HR from '../HorizontalRule';
import {
  TitleBar,
  DocTitle,
} from '../../../../common/components/Layout/StandardPageTemplate/Context';
import withCustomerOrg from '../../../../common/components/withCustomerOrg';
import ThemedInput from '../../../../common/components/ThemedInput';
import { withLoading } from '../../../../redux-modules/loading-overlay';
import composes from '../../../../common/utils/composes';
import StatesList from '../../../../common/states.json';
import CustomerActions from '../../actions/customers';
import OrganizationActions from '../../actions/organizations';
import { getCustomer } from '../../selectors/customers';
import { getOrganization } from '../../selectors/organizations';
import BusinessEdit from './BusinessEdit';
import PersonalEdit from './PersonalEdit';
import MembershipCard from '../MembershipCard';
import styles from './MembershipInfo.css';
import toolkit from '../../../../common/utils/toolkit';

const RenewButton = ({ userId }) => (
  <Button
    onClick={() => (window.location.href = `/redirect/renew?cst=${userId}`)}
    raised
    primary
    label="renew"
    className={styles.renewButton}
  />
);
RenewButton.propTypes = {
  userId: PropTypes.string,
};

export const buildAddress = path => {
  return [
    {
      Component: ThemedInput,
      name: `${path}.addressLine1`,
      label: 'Address Line 1',
      type: 'text',
      maxLength: 50,
    },
    {
      Component: ThemedInput,
      name: `${path}.addressLine2`,
      label: 'Address Line 2',
      type: 'text',
      maxLength: 50,
    },
    {
      Component: ThemedInput,
      name: `${path}.city`,
      type: 'text',
      maxLength: 40,
    },
    {
      Component: Dropdown,
      name: `${path}.stateCode`,
      label: 'State',
      auto: true,
      source: StatesList.map(state => ({
        value: state.id,
        label: state.name,
      })),
    },
    {
      Component: ThemedInput,
      name: `${path}.postalCode`,
      label: 'Zip Code',
      type: 'text',
      onChange: value => formatZipCode(value),
    },
  ];
};

class MembershipInfo extends React.Component {
  static propTypes = {
    currentUser: PropTypes.shape({
      id: PropTypes.string,
    }),
    currentCustomer: PropTypes.object,
    currentOrganization: PropTypes.object,
    actions: PropTypes.shape({
      customer: PropTypes.objectOf(PropTypes.func),
      organization: PropTypes.objectOf(PropTypes.func),
    }),
  };
  static contextTypes = {
    activateMemberhipCard: PropTypes.func,
  };
  static promises = {
    includePromises: [
      {
        meta: { namespace: 'customers' },
      },
      {
        meta: { namespace: 'organizations' },
      },
    ],
  };
  state = {
    tabIndex: 0,
  };

  handleTabChange = tabIndex => {
    this.setState({ tabIndex });
  };

  renderMembershipQuickInfo = () => {
    const { currentUser, currentOrganization } = this.props;
    if (!currentUser || !currentOrganization) {
      return null;
    }

    const {
      membershipDetails = {},
      organizationDetails = {},
      id = '',
    } = currentOrganization;
    
    var orgName = organizationDetails.name;

    ////The toolkit is populated by the database(when available), not the local store. Allow the database values to default, not the local store.
    if(toolkit.organizationDetails!=null&&toolkit.organizationDetails.name!=null&&toolkit.organizationDetails.name!==''){
      orgName = toolkit.organizationDetails.name;
    }
    const isLapsed = membershipDetails.statusType === MembershipStatus.Lapsed;
    let memberDetails = [];
    if (currentUser.type === 'PrimaryContact') {
      memberDetails = [
        {
          label: 'Member Since',
          value: moment(membershipDetails.originalJoinDate).format(
            'MM/DD/YYYY'
          ),
        },
        {
          label: isLapsed ? 'Termination Date' : 'Renewal Date',
          value: isLapsed
            ? moment(membershipDetails.terminationDate).format('MM/DD/YYYY')
            : moment(membershipDetails.renewalDate).format('MM/DD/YYYY'),
        },
        {
          label: 'Member ID',
          value: id,
        },
      ];
    }
    if (currentUser.type === 'SecondaryContact') {
      memberDetails = [
        {
          label: 'Company Name',
          value: orgName,
        },
      ];
    }
    if (memberDetails.length) {
      return (
        <div className={styles.memberQuickInfo}>
          <Card className={styles.membershipDeetsCard}>
            <CardText className={styles.deetsCardText}>
              <div className={styles.membershipDeets}>
                <h3 className={styles.deetsHeader}>
                  <div>
                    <FontIcon
                      className={styles.deetsIcon}
                      value="card_membership"
                      style={{ marginRight: 6 }}
                    />
                    <span style={{ verticalAlign: 'top' }}>
                      Membership Details
                    </span>
                  </div>
                </h3>
                {isLapsed && (
                  <Media
                    query="(min-width: 701px)"
                    render={() => <RenewButton userId={currentUser.id} />}
                  />
                )}
                {this.renderMemberDetailTable(memberDetails)}
                {isLapsed && (
                  <Media
                    query="(max-width: 700px)"
                    render={() => <RenewButton userId={currentUser.id} />}
                  />
                )}
              </div>
            </CardText>
          </Card>
          {currentUser.type === 'PrimaryContact' ? (
            <Card
              className={styles.membershipCard}
              onClick={this.context.activateMemberhipCard}
            >
              <MembershipCard
                businessName={orgName}
                memberId={id}
                memberSince={moment(membershipDetails.originalJoinDate).format(
                  'MM/DD/YYYY'
                )}
              />
            </Card>
          ) : null}
        </div>
      );
    }
  };

  renderMemberDetailTable = details => {
    return (
      <div className={styles.flexTable}>
        {details.map((detail, index) => {
          return (
            <div
              className={styles.flexTableRow}
              key={`${detail.label}_${index}`}
            >
              <div key={`label_${index}`} className={styles.flexTableLabel}>
                {detail.label}
              </div>
              <div key={`value_${index}`} className={styles.flexTableValue}>
                {detail.value}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderPersonalDetailsEdit = () => {
    return (
      <PersonalEdit
        currentCustomer={this.props.currentCustomer}
        actions={this.props.actions}
      />
    );
  };

  renderTabbedView = () => {
    return (
      <Tabs fixed index={this.state.tabIndex} onChange={this.handleTabChange}>
        <Tab label="Personal">{this.renderPersonalDetailsEdit()}</Tab>
        <Tab label="Business">
          <BusinessEdit
            currentOrganization={this.props.currentOrganization}
            actions={this.props.actions}
          />
        </Tab>
      </Tabs>
    );
  };

  render() {
    const { currentUser, currentOrganization } = this.props;
    const isMember =
      currentOrganization &&
      currentOrganization.membershipDetails.type === MembershipType.Member;
    const pageTitle = 'Membership Information';
    return (
      <div>
        <TitleBar title={pageTitle} />
        <DocTitle title={pageTitle} />
        {isMember ? this.renderMembershipQuickInfo() : null}
        <Card className={styles.membershipDeetsCard}>
          {currentUser.type === 'PrimaryContact'
            ? this.renderTabbedView()
            : this.renderPersonalDetailsEdit()}
        </Card>
        <HR color="blue" />
        <ButtonLink
          primary
          raised
          to="/account"
          label="Back to Account Management"
        />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const currentUser = getCurrentUser(state);
  const currentCustomer = getCustomer(state, { id: currentUser.id });
  let currentOrganization;
  if (
    currentCustomer &&
    currentCustomer.organization &&
    currentCustomer.organization.key
  ) {
    currentOrganization = getOrganization(state, {
      id: currentCustomer.organization.key,
    });
  }

  return {
    currentUser,
    currentCustomer,
    currentOrganization,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: {
      customer: bindActionCreators(CustomerActions, dispatch),
      organization: bindActionCreators(OrganizationActions, dispatch),
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(
  composes(MembershipInfo, withLoading, withCustomerOrg)
);



// WEBPACK FOOTER //
// ./src/App/account/components/MembershipInfo/index.js